import { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Sparkles, ChevronUp } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export default function AuraChat({ scanId, autoOpen = false, proactiveMessage }: { scanId: string; autoOpen?: boolean; proactiveMessage?: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadSuggestions();
    }
  }, [isOpen]);

  // Auto-open with proactive message when scan completes
  useEffect(() => {
    if (autoOpen && !hasAutoOpened && scanId) {
      setHasAutoOpened(true);
      setIsOpen(true);
      if (proactiveMessage) {
        setMessages([{ role: 'assistant', content: proactiveMessage, timestamp: new Date().toISOString() }]);
      } else {
        // Auto-ask for summary
        setTimeout(() => sendMessage('Dame un resumen rapido de los hallazgos mas importantes'), 500);
      }
    }
  }, [autoOpen, scanId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const API = import.meta.env.VITE_API_URL || 'https://xfreescan-api.aforero.workers.dev/api';
  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('xfreescan_token') || ''}`,
    'Content-Type': 'application/json',
  });

  const loadSuggestions = async () => {
    try {
      const resp = await fetch(`${API}/ai/suggestions/${scanId}`, { headers: authHeaders() });
      const data = await resp.json();
      if (data?.data?.suggestions) setSuggestions(data.data.suggestions);
    } catch (e) {
      console.error('[AURA] suggestions error:', e);
    }
  };

  const sendMessage = async (text: string) => {
    if (!text.trim()) return;
    const userMsg: Message = { role: 'user', content: text.trim(), timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setSuggestions([]);

    try {
      const resp = await fetch(`${API}/ai/analyze`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ scan_id: scanId, question: text.trim() }),
      });
      const data = await resp.json();
      const answer = data?.data?.answer || data?.error || 'Sin respuesta. Intente de nuevo.';
      setMessages(prev => [...prev, { role: 'assistant', content: answer, timestamp: new Date().toISOString() }]);
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Error de conexion. Intente de nuevo.', timestamp: new Date().toISOString() }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-gradient-to-r from-purple-600 to-purple-700 px-5 py-3 text-white shadow-xl shadow-purple-500/25 hover:shadow-purple-500/40 transition-all hover:scale-105 print:hidden"
      >
        <Sparkles className="h-5 w-5" />
        <span className="font-semibold text-sm">Preguntar a AURA</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-96 max-h-[600px] flex flex-col rounded-2xl border border-purple-200 bg-white shadow-2xl print:hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-600 to-purple-700 rounded-t-2xl">
        <div className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-white" />
          <div>
            <p className="text-sm font-bold text-white">AURA — AI Security Analyst</p>
            <p className="text-[10px] text-purple-200">Pregunte sobre sus hallazgos</p>
          </div>
        </div>
        <button onClick={() => setIsOpen(false)} className="text-white/70 hover:text-white">
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[400px] min-h-[200px]">
        {messages.length === 0 && (
          <div className="text-center py-4">
            <Sparkles className="h-8 w-8 text-purple-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">Soy AURA, su analista de seguridad AI.</p>
            <p className="text-xs text-gray-400 mt-1">Pregunte sobre los hallazgos de este escaneo.</p>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] rounded-xl px-3 py-2 text-sm ${
              msg.role === 'user'
                ? 'bg-purple-600 text-white rounded-br-none'
                : 'bg-gray-100 text-gray-800 rounded-bl-none'
            }`}>
              <div className="whitespace-pre-wrap break-words" style={{ fontSize: '13px', lineHeight: '1.5' }}>
                {msg.content}
              </div>
            </div>
          </div>
        ))}

        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-xl px-4 py-3 rounded-bl-none">
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <div className="flex gap-1">
                  <div className="h-2 w-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <div className="h-2 w-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <div className="h-2 w-2 rounded-full bg-purple-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
                Analizando...
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && messages.length === 0 && (
        <div className="px-4 pb-2">
          <p className="text-[10px] text-gray-400 uppercase font-medium mb-1.5">Preguntas sugeridas</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.slice(0, 4).map((s, i) => (
              <button key={i} onClick={() => sendMessage(s)}
                className="text-[11px] px-2.5 py-1 rounded-full border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors text-left">
                {s}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-gray-200 p-3 flex gap-2">
        <input
          ref={inputRef}
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          placeholder="Pregunte sobre los hallazgos..."
          disabled={isLoading}
          className="flex-1 text-sm rounded-xl border border-gray-300 px-3 py-2 focus:border-purple-500 focus:outline-none focus:ring-1 focus:ring-purple-500/20 disabled:opacity-50"
        />
        <button
          type="submit"
          disabled={isLoading || !input.trim()}
          className="rounded-xl bg-purple-600 px-3 py-2 text-white hover:bg-purple-700 disabled:opacity-50 transition-colors"
        >
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
}
